<template>
    <div>
        <minisite-header />

        <main class="container transfer-start">
            <div class="row transfer-start__page-title">
                <div class="col-12">
                    <h3>
                        {{ say.transfer_start_page_title || 'Przekaż swój start innej osobie' }}
                    </h3>
                </div>
            </div>

            <!-- Loading -->
            <template v-if="!ready">
                <div class="row transfer-start__section">
                    <div class="col-12 col-lg-4">
                        <div class="transfer-start__section-header transfer-start__section-header--skeleton" />
                    </div>
                    <div class="col-12 col-lg-8">
                        <div class="transfer-start__card bg--gray transfer-start__card--skeleton" />
                    </div>
                </div>
                <div class="row transfer-start__section">
                    <div class="col-12 col-lg-4">
                        <div class="transfer-start__section-header transfer-start__section-header--skeleton" />
                    </div>
                    <div class="col-12 col-lg-8">
                        <div
                            class="transfer-start__card bg--gray transfer-start__card--skeleton transfer-start__card--short"
                        />
                    </div>
                </div>
                <p class="transfer-start__loading-text mat16">
                    {{ say.transfer_start_loading || 'Ładowanie…' }}
                </p>
            </template>

            <!-- Error -->
            <template v-else-if="errorMessage && !canTransfer">
                <div class="row transfer-start__section">
                    <div class="col-12 col-lg-4">
                        <div class="transfer-start__section-header">
                            <h3>
                                {{ say.transfer_start_error_title || 'Przekazanie startu' }}
                            </h3>
                        </div>
                    </div>
                    <div class="col-12 col-lg-8">
                        <div class="transfer-start__card bg--gray">
                            <p class="transfer-start__error">
                                {{ errorMessage }}
                            </p>
                        </div>
                    </div>
                </div>
                <div class="row transfer-start__section transfer-start__actions">
                    <div class="col-12 col-lg-4 d-none d-lg-block" />
                    <div class="col-12 col-lg-8">
                        <div class="transfer-start__buttons">
                            <base-button btn2 short @click="goBack()">
                                {{ say.transfer_start_btn_back || 'Cofnij' }}
                            </base-button>
                            <base-button btn1 short @click="goToCompetitions()">
                                {{ say.transfer_start_btn_back_to_competitions || 'Wróć do zawodów' }}
                            </base-button>
                        </div>
                    </div>
                </div>
            </template>

            <!-- Success -->
            <template v-else-if="rebateCode">
                <div class="row transfer-start__section">
                    <div class="col-12 col-lg-4">
                        <div class="transfer-start__section-header">
                            <h3>
                                {{ say.transfer_start_rules_title || 'Zasady przekazywania startu' }}
                            </h3>
                        </div>
                    </div>
                    <div class="col-12 col-lg-8">
                        <div class="transfer-start__card bg--gray">
                            <h3 class="transfer-start__content-title">
                                {{ say.transfer_start_cannot_start_title || 'Nie możesz wystartować?' }}
                            </h3>
                            <p>
                                {{
                                    say.transfer_start_success_intro ||
                                    'Mail z linkiem do rejestracji został wysłany. Poniżej znajdziesz kod przekazania startu — udostępnij go zaproszonej osobie.'
                                }}
                            </p>
                            <div class="transfer-start__code mat24">
                                <p class="transfer-start__code-label">
                                    {{ say.transfer_start_code_label || 'Kod przekazania startu' }}
                                </p>
                                <p class="transfer-start__code-value">{{ rebateCode }}</p>
                            </div>
                            <p class="mat16">
                                {{
                                    say.transfer_start_success_followup ||
                                    'Zaproszona osoba rejestruje się z linku, wpisuje kod w polu kodu rabatowego'
                                }}
                                <template v-if="isFree">
                                    {{
                                        say.transfer_start_success_outro_free ||
                                        ' i bezpłatnie finalizuje przejęcie startu.'
                                    }}
                                </template>
                                <template v-else>
                                    {{ say.transfer_start_success_outro_paid || ' i opłaca online koszt przekazania.' }}
                                </template>
                                {{
                                    say.transfer_start_success_settlement ||
                                    ' Rozliczenie ewentualnego zwrotu opłaty startowej między Wami odbywa się poza systemem.'
                                }}
                            </p>
                        </div>
                    </div>
                </div>
                <div class="row transfer-start__section transfer-start__actions">
                    <div class="col-12 col-lg-4 d-none d-lg-block" />
                    <div class="col-12 col-lg-8">
                        <div class="transfer-start__buttons transfer-start__buttons--end">
                            <base-button btn1 short @click="goToCompetitions()">
                                {{ say.transfer_start_btn_back_to_competitions || 'Wróć do zawodów' }}
                            </base-button>
                        </div>
                    </div>
                </div>
            </template>

            <!-- Form -->
            <template v-else>
                <div class="row transfer-start__section">
                    <div class="col-12 col-lg-4">
                        <div class="transfer-start__section-header">
                            <h3>
                                {{ say.transfer_start_rules_title || 'Zasady przekazywania startu' }}
                            </h3>
                        </div>
                    </div>
                    <div class="col-12 col-lg-8">
                        <div class="transfer-start__card bg--gray">
                            <h3 class="transfer-start__content-title">
                                {{ say.transfer_start_cannot_start_title || 'Nie możesz wystartować?' }}
                            </h3>

                            <template v-if="isFree">
                                <p>
                                    {{
                                        say.transfer_start_intro_free ||
                                        'Przekaż swój opłacony start innej osobie. Oto jak to działa:'
                                    }}
                                </p>
                                <ul class="transfer-start__steps">
                                    <li>
                                        {{
                                            say.transfer_start_step_1 ||
                                            'Podajesz adres e-mail osoby, której chcesz przekazać start.'
                                        }}
                                    </li>
                                    <li>
                                        {{
                                            say.transfer_start_step_2 ||
                                            'Osoba ta otrzymuje e-mail z linkiem do rejestracji oraz informacją o koszcie i terminie ważności przekazania.'
                                        }}
                                    </li>
                                    <li>
                                        {{
                                            say.transfer_start_step_3 ||
                                            'Ty otrzymujesz kod przekazania startu — udostępniasz go zaproszonej osobie (rozliczenie ewentualnego zwrotu opłaty startowej między Wami odbywa się poza systemem).'
                                        }}
                                    </li>
                                    <li>
                                        {{
                                            say.transfer_start_step_4_free ||
                                            'Zaproszona osoba rejestruje się z linku, wpisuje kod w polu kodu rabatowego i bezpłatnie finalizuje przejęcie startu.'
                                        }}
                                    </li>
                                </ul>
                                <p class="mat16">
                                    <strong>
                                        {{ say.transfer_start_free_label || 'Przekazanie startu jest bezpłatne.' }}
                                    </strong>
                                </p>
                            </template>
                            <template v-else>
                                <p>
                                    {{
                                        say.transfer_start_intro_paid ||
                                        'Możesz przekazać swój opłacony start innej osobie.'
                                    }}
                                </p>
                                <p>
                                    {{ say.transfer_start_how_it_works || 'Oto jak to działa:' }}
                                </p>
                                <ul class="transfer-start__steps">
                                    <li>
                                        {{
                                            say.transfer_start_step_1 ||
                                            'Podajesz adres e-mail osoby, której chcesz przekazać start.'
                                        }}
                                    </li>
                                    <li>
                                        {{
                                            say.transfer_start_step_2 ||
                                            'Osoba ta otrzymuje e-mail z linkiem do rejestracji oraz informacją o koszcie i terminie ważności przekazania.'
                                        }}
                                    </li>
                                    <li>
                                        {{
                                            say.transfer_start_step_3 ||
                                            'Ty otrzymujesz kod przekazania startu — udostępniasz go zaproszonej osobie (rozliczenie zwrotu opłaty startowej między Wami odbywa się poza systemem).'
                                        }}
                                    </li>
                                    <li>
                                        {{
                                            say.transfer_start_step_4_paid ||
                                            'Zaproszona osoba rejestruje się z linku, wpisuje kod w polu kodu rabatowego i opłaca online koszt przekazania.'
                                        }}
                                    </li>
                                </ul>
                                <p class="mat16">
                                    {{ say.transfer_start_paid_cost_prefix || 'Koszt przekazania startu:' }}
                                    <strong>{{ transferPriceLabel }} zł</strong>
                                    {{
                                        say.transfer_start_paid_cost_suffix ||
                                        ' (opłaca osoba przejmująca start podczas rejestracji). Ty nie ponosisz dodatkowych kosztów.'
                                    }}
                                </p>
                            </template>
                            <p>
                                {{
                                    say.transfer_start_deadline_prefix || 'Termin: start możesz przekazać najpóźniej do'
                                }}
                                {{ deadlineLabel }}.
                            </p>
                        </div>
                    </div>
                </div>

                <div class="row transfer-start__section">
                    <div class="col-12 col-lg-4">
                        <div class="transfer-start__section-header">
                            <h3>
                                {{ say.transfer_start_email_section_title || 'Adres e-mail osoby,' }}
                            </h3>
                            <p>
                                {{ say.transfer_start_email_section_description || 'której przekazujesz swój udział' }}
                            </p>
                        </div>
                    </div>
                    <div class="col-12 col-lg-8">
                        <div class="transfer-start__card bg--gray">
                            <base-input-text
                                v-model="email"
                                type="email"
                                :label="say.transfer_start_email_label || 'Adres e-mail odbiorcy'"
                                :hint="say.transfer_start_email_hint || 'np. jan@example.com'"
                                :error="emailError"
                                without-error-message
                            />
                            <p v-if="emailError" class="transfer-start__error mat8">
                                {{ emailError }}
                            </p>
                            <p v-if="submitError" class="transfer-start__error mat8">
                                {{ submitError }}
                            </p>
                        </div>
                    </div>
                </div>

                <div class="row transfer-start__section transfer-start__actions">
                    <div class="col-12 col-lg-4 d-none d-lg-block">&nbsp;</div>
                    <div class="col-12 col-lg-8">
                        <div class="transfer-start__buttons">
                            <base-button btn3 short @click="goBack()">
                                {{ say.transfer_start_btn_back || 'Cofnij' }}
                            </base-button>
                            <base-button
                                btn1
                                short
                                style="min-width: 260px"
                                :loading="isSubmitting"
                                :disabled="isSubmitting"
                                @click="submit()"
                            >
                                {{ say.transfer_start_btn_send || 'Wyślij' }}
                            </base-button>
                        </div>
                    </div>
                </div>
            </template>
        </main>
    </div>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex';
    import moment from 'moment';
    import actions from '@/mixins/actions';
    import MinisiteHeader from '@/components/MinisiteHeader';
    import { BOOTSTRAP_MINISITE, READ_PLAYER, TRANSFER_PLAYER_START } from '@/store/actions.type';

    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    export default {
        components: {
            MinisiteHeader,
        },
        mixins: [actions],
        props: {
            competitionId: {
                type: Number,
                required: true,
            },
            playerId: {
                type: Number,
                required: true,
            },
        },
        data() {
            return {
                ready: false,
                email: '',
                emailError: null,
                submitError: null,
                isSubmitting: false,
                rebateCode: null,
                errorMessage: null,
            };
        },
        computed: {
            ...mapGetters(['say', 'user', 'competitions', 'players']),
            competition() {
                return this.competitions[this.competitionId];
            },
            player() {
                return this.players[this.playerId];
            },
            canTransfer() {
                return (
                    this.user &&
                    this.player &&
                    this.competition &&
                    this.player.user === this.user.id &&
                    this.canPlayerTransferStart(this.player, this.competition)
                );
            },
            isFree() {
                return Number(this.competition?.transferPrice || 0) === 0;
            },
            transferPriceLabel() {
                const price = Number(this.competition?.transferPrice || 0);
                return Number.isInteger(price) ? String(price) : price.toFixed(2);
            },
            deadlineLabel() {
                const deadline = this.competition?.transferDeadline || this.competition?.startedTime;
                if (!deadline) {
                    return this.say.transfer_start_deadline_fallback || 'rozpoczęcia zawodów';
                }
                const m = moment(deadline);
                return `${this.say.transfer_start_deadline_event || 'rozpoczęcia zawodów'} ${m.format('DD.MM.YYYY')}, ${
                    this.say.transfer_start_deadline_hour || 'godz.'
                } ${m.format('HH:mm')}`;
            },
        },
        watch: {
            competitionId: {
                immediate: true,
                handler() {
                    this.bootstrap();
                },
            },
            playerId() {
                this.bootstrap();
            },
            email() {
                this.emailError = null;
            },
        },
        methods: {
            ...mapActions([BOOTSTRAP_MINISITE, READ_PLAYER, TRANSFER_PLAYER_START]),
            goBack() {
                this.$router.back();
            },
            goToCompetitions() {
                this.$router.push({
                    name: 'user-competitions-future',
                    alias: true,
                });
            },
            async bootstrap() {
                this.ready = false;
                this.rebateCode = null;
                this.errorMessage = null;
                this.email = '';
                this.emailError = null;
                this.submitError = null;

                if (!this.user || !this.user.roles.includes('ROLE_USER')) {
                    this.errorMessage =
                        this.say.transfer_start_error_login || 'Musisz być zalogowany, aby przekazać start.';
                    this.ready = true;
                    this.$router.replace({
                        name: 'authentication-login',
                        query: { redirect: this.$route.fullPath },
                        alias: true,
                    });
                    return;
                }

                try {
                    await Promise.all([this.BOOTSTRAP_MINISITE(this.competitionId), this.READ_PLAYER(this.playerId)]);
                } catch (e) {
                    this.errorMessage = this.say.transfer_start_error_load || 'Nie udało się wczytać danych startu.';
                    this.ready = true;
                    return;
                }

                if (!this.player || this.player.user !== this.user.id) {
                    this.errorMessage =
                        this.say.transfer_start_error_not_owner || 'Ten start nie należy do Twojego konta.';
                    this.ready = true;
                    return;
                }

                if (!this.canPlayerTransferStart(this.player, this.competition)) {
                    this.errorMessage =
                        this.say.transfer_start_error_unavailable ||
                        'Przekazanie startu jest niedostępne dla tych zawodów.';
                    this.ready = true;
                    return;
                }

                this.ready = true;
            },
            submit() {
                this.emailError = null;
                this.submitError = null;
                const email = (this.email || '').trim();

                if (!email) {
                    this.emailError = this.say.transfer_start_error_email_required || 'Podaj adres e-mail.';
                    return;
                }
                if (!EMAIL_RE.test(email)) {
                    this.emailError = this.say.transfer_start_error_email_invalid || 'Podaj poprawny adres e-mail.';
                    return;
                }

                this.send(email);
            },
            async send(email) {
                this.isSubmitting = true;
                try {
                    const response = await this.TRANSFER_PLAYER_START({
                        playerId: this.playerId,
                        email,
                    });
                    this.rebateCode = response?.rebateCode || null;
                    if (!this.rebateCode) {
                        this.submitError =
                            this.say.transfer_start_error_no_code ||
                            'Wysłano zaproszenie, ale nie otrzymano kodu. Skontaktuj się z organizatorem.';
                    }
                } catch (e) {
                    this.submitError =
                        (typeof e === 'string' && e) ||
                        e?.error ||
                        e?.message ||
                        e?.email ||
                        this.say.transfer_start_error_submit ||
                        'Nie udało się wysłać zaproszenia. Spróbuj ponownie.';
                } finally {
                    this.isSubmitting = false;
                }
            },
        },
    };
</script>

<style scoped>
    .transfer-start {
        padding-bottom: 64px;
    }
    .transfer-start__page-title {
        margin-top: 40px;
        margin-bottom: 40px;
    }
    .transfer-start__page-title h3 {
        margin: 0;
    }
    .transfer-start__section + .transfer-start__section {
        margin-top: 24px;
    }
    .transfer-start__section-header h3 {
        margin: 0;
        font-size: 18px;
        line-height: 24px;
    }
    .transfer-start__card {
        border-radius: 4px;
        padding: 32px;
    }
    .transfer-start__card--skeleton {
        min-height: 220px;
        animation: transfer-start-pulse 1.2s ease-in-out infinite;
    }
    .transfer-start__card--short {
        min-height: 80px;
    }
    .transfer-start__section-header--skeleton {
        min-height: 24px;
        width: 70%;
        border-radius: 4px;
        background-color: var(--color-pale-grey, #edf0f1);
        animation: transfer-start-pulse 1.2s ease-in-out infinite;
    }
    .transfer-start__loading-text {
        color: var(--color-greyish-brown, #4a4a4a);
    }
    .transfer-start__content-title {
        margin: 0 0 16px;
    }
    .transfer-start__steps {
        padding-left: 20px;
        line-height: 1.5;
        margin: 16px 0;
    }
    .transfer-start__steps li {
        margin-bottom: 8px;
    }
    .transfer-start__code {
        border: 1px solid var(--color-dark-pastel-green, #6a9e6a);
        border-radius: 4px;
        padding: 16px;
        text-align: center;
        background: rgba(106, 158, 106, 0.08);
    }
    .transfer-start__code-label {
        margin-bottom: 8px;
        font-size: 14px;
    }
    .transfer-start__code-value {
        font-size: 28px;
        font-weight: 700;
        letter-spacing: 0.04em;
        margin: 0;
    }
    .transfer-start__error {
        color: var(--color-negative, #c0392b);
        margin: 0;
    }
    .transfer-start__actions {
        margin-top: 48px;
    }
    .transfer-start__buttons {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
    }
    .transfer-start__buttons--end {
        justify-content: flex-end;
    }
    @keyframes transfer-start-pulse {
        0%,
        100% {
            opacity: 1;
        }
        50% {
            opacity: 0.55;
        }
    }
    @media (max-width: 991px) {
        .transfer-start__section-header {
            margin-bottom: 16px;
        }
        .transfer-start__card {
            padding: 24px 16px;
        }
        .transfer-start__buttons {
            flex-direction: column-reverse;
            align-items: stretch;
        }
    }
    @media (min-width: 992px) {
        .d-lg-block {
            display: block;
        }
    }
</style>
